import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  DateInput,
  DatePicker as AriaDatePicker,
  DateRangePicker as AriaDateRangePicker,
  DateSegment,
  Group,
  Popover,
  DateValue,
  I18nProvider,
} from 'react-aria-components';
import { RangeValue } from '@react-types/shared';
import { getLocalTimeZone, parseDate } from '@internationalized/date';

import Calendar from '@components/Calendar';
import RangeCalendar from '@components/RangeCalendar';

import { ReactComponent as CalendarIcon } from 'img/icon_calendar.svg';
import { ReactComponent as CloseIcon } from 'img/btn-close.svg';
import './DatePicker.scss';

const formatDate = (date: DateValue | null | undefined) => {
  if (!date) return null;
  const timeZone = getLocalTimeZone();
  return dayjs(date.toDate(timeZone)).format('YYYY-MM-DD');
};

export function DatePicker({
  value,
  onChange,
  className,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
}) {
  const [date, setDate] = useState<DateValue | null | undefined>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    onChange(formatDate(date));
  }, [date, onChange]);

  useEffect(() => {
    if (value) {
      setDate(parseDate(value));
    } else {
      setDate(null);
    }
  }, [value]);

  const handleDateChange = (newDate: DateValue | null) => {
    setDate(newDate);
    setOpen(false); // 날짜 선택 후 팝오버 닫기
  };

  return (
    <I18nProvider locale="en-US">
      <AriaDatePicker
        className={`date-picker-container ${className ?? ''}`}
        value={date}
        onChange={handleDateChange}
        aria-label="date picker"
      >
        <Group className="date-picker-group">
          <DateInput
            className={`date-input${date ? ' active' : ''}`}
            slot="start"
          >
            {(segment) => <DateSegment segment={segment} />}
          </DateInput>
          {/* {date && (
          <CloseIcon className="date-picker-clear" width="14px" onClick={() => setDate(null)} />
        )} */}
          <div className="date-picker-button" onClick={() => setOpen(!open)}>
            <CalendarIcon />
          </div>
        </Group>
        {open && (
          <Popover className="popover1" onOpenChange={setOpen} isOpen={open}>
            <Calendar value={date} onChange={handleDateChange} />
          </Popover>
        )}
      </AriaDatePicker>
    </I18nProvider>
  );
}

function formatDate2(date?: DateValue | null): string {
  if (!date) return '';
  const yyyy = date.year.toString();
  const mm = String(date.month).padStart(2, '0');
  const dd = String(date.day).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function DateRangePicker({
  className,
  value,
  position = 'bottom',
  onChange,
}: {
  className?: string;
  value: { startDate: string | null; endDate: string | null };
  position?: 'top' | 'bottom';
  onChange: (value: {
    startDate: string | null;
    endDate: string | null;
  }) => void;
}) {
  const [range, setRange] = useState<RangeValue<DateValue> | null | undefined>(
    null,
  );
  const [open, setOpen] = useState(false);

  const handleRangeChange = (newRange?: RangeValue<DateValue> | null) => {
    if (!newRange) {
      setRange(null);
      onChange({ startDate: null, endDate: null });
      return;
    }
    let { start, end } = newRange;
    if (end && start && end.compare(start) < 0) {
      end = start;
    }
    setRange({ start, end });
    onChange({
      startDate: formatDate(start),
      endDate: formatDate(end),
    });
  };

  useEffect(() => {
    if (value.startDate === null && value.endDate === null) {
      setRange(null);
    }
  }, [value]);

  return (
    <I18nProvider locale="en-US">
      <AriaDateRangePicker
        className={`date-picker-container ${className ?? ''}`}
        value={range}
        onChange={handleRangeChange}
        aria-label="date picker"
      >
        <Group className="date-picker-group">
          <DateInput
            className={`date-input${range ? ' active' : ''}`}
            slot="start"
          >
            {(segment) => <DateSegment segment={segment} />}
          </DateInput>
          <span
            className={`date-input${range ? ' active' : ''}`}
            aria-hidden="true"
          >
            ~
          </span>
          <DateInput
            className={`date-input${range ? ' active' : ''}`}
            slot="end"
          >
            {(segment) => <DateSegment segment={segment} />}
          </DateInput>
          {/* <div className={`date-picker-placeholder ${range && range.start && range.end ? 'has-value' : ''}`}>
          {range && range.start && range.end
            ? `${formatDate2(range.start)} ~ ${formatDate2(range.end)}`
            : "Filter by Date"}
        </div> */}
          {/* {date && (
          <CloseIcon className="date-picker-clear" width="14px" onClick={() => setDate(null)} />
        )} */}
          <div className="date-picker-button" onClick={() => setOpen(!open)}>
            <CalendarIcon />
          </div>
        </Group>
        {open && (
          <Popover
            className={position === 'top' ? 'popover2' : 'popover1'}
            onOpenChange={setOpen}
            isOpen={open}
          >
            <RangeCalendar value={range} onChange={handleRangeChange} />
          </Popover>
        )}
      </AriaDateRangePicker>
    </I18nProvider>
  );
}
